import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API, getAccessToken } from "@/lib/api";

/**
 * Phase 18.3 — Universal "Export CSV" button. Streams the CSV response
 * from the backend and hands it to the browser as a file download.
 *
 * Props:
 *   endpoint  — path AFTER /api (e.g., "tenant/timesheets/export.csv"
 *               or "master/tenants/<id>/timesheets/export.csv").
 *   filename  — suggested filename for the download.
 *   filters   — object serialized to querystring (status/period_start/...).
 *   testId    — data-testid to expose to tests.
 *   label     — display label; defaults to "Export CSV".
 */
export default function ExportCsvButton({
  endpoint,
  filename = "timesheets.csv",
  filters = {},
  testId = "export-csv-btn",
  label = "Export CSV",
  variant = "outline",
  size = "sm",
  disabled = false,
}) {
  const [busy, setBusy] = useState(false);

  const trigger = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const qs = new URLSearchParams(
        Object.entries(filters).filter(([, v]) => v !== undefined && v !== null && v !== ""),
      ).toString();
      const url = `${API}/${endpoint.replace(/^\//, "")}${qs ? `?${qs}` : ""}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${getAccessToken()}` },
      });
      if (!res.ok) {
        let detail = `Export failed (HTTP ${res.status})`;
        try {
          const body = await res.json();
          if (body?.detail) detail = typeof body.detail === "string" ? body.detail : JSON.stringify(body.detail);
        } catch (_) {
          /* stream not JSON */
        }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(e?.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={trigger}
      disabled={busy || disabled}
      data-testid={testId}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
      <span className="ml-1.5">{label}</span>
    </Button>
  );
}
